import { getWallets } from "@wallet-standard/app";
import type { Wallet, WalletAccount } from "@wallet-standard/base";
import {
  PublicKey,
  Transaction,
  VersionedTransaction,
} from "@solana/web3.js";
import {
  findLegacyProviders, isLegacyId, legacyId, legacyNameFromId,
  type LegacyProvider,
} from "./wallet-legacy";

/**
 * Wallet integration via the Wallet Standard.
 *
 * This app NEVER accepts, stores or transmits a private key. Every signature
 * comes from the user's wallet extension. There is no code path that reads a
 * keypair, and there is deliberately no "paste your seed phrase" affordance --
 * anything that asks for one is a phishing page, including a page that looks
 * like this one.
 */

const CONNECT = "standard:connect";
const DISCONNECT = "standard:disconnect";
const SIGN_TX = "solana:signTransaction";
const SIGN_AND_SEND = "solana:signAndSendTransaction";

export type AnyTx = Transaction | VersionedTransaction;

export interface WalletInfo {
  name: string;
  icon: string;
  /** Opaque handle; pass back to connectWallet. */
  id: string;
}

export interface ConnectedWallet {
  name: string;
  icon: string;
  publicKey: PublicKey;
  chains: readonly string[];
  signAllTransactions(txs: AnyTx[]): Promise<AnyTx[]>;
  disconnect(): Promise<void>;
}

function hasFeature(w: Wallet, name: string): boolean {
  return Object.prototype.hasOwnProperty.call(w.features, name);
}

function usable(w: Wallet): boolean {
  return (
    hasFeature(w, CONNECT) &&
    (hasFeature(w, SIGN_TX) || hasFeature(w, SIGN_AND_SEND)) &&
    w.chains.some((c) => c.startsWith("solana:"))
  );
}

export function listWallets(): WalletInfo[] {
  const standard = getWallets()
    .get()
    .filter(usable)
    .map((w) => ({ name: w.name, icon: w.icon, id: w.name }));

  /*
      Then the wallets that never registered. A wallet that exposes itself only
      as an injected provider -- Brave's built-in one, among others -- is
      invisible to the Wallet Standard registry, and the console used to tell
      that browser it had no wallet at all.

      A standard registration WINS on name: if a wallet offers both, the
      standard interface is the better-specified one and offering the same
      wallet twice makes the operator guess which button is real.
  */
  const claimed = new Set(standard.map((w) => w.name));
  const legacy = findLegacyProviders(typeof window === "undefined" ? null : window)
    .filter((l) => !claimed.has(l.name))
    .map((l) => ({ name: l.name, icon: "", id: legacyId(l.name) }));

  return [...standard, ...legacy];
}

/**
 * Why a registered wallet was filtered out, or null when it was not.
 *
 * `usable()` answers a boolean, and a boolean is what made the UI say "No
 * Solana wallet detected" to somebody who plainly had one installed. The
 * reason is the useful part.
 */
export function rejectionReason(w: Wallet): string | null {
  if (!w.chains.some((c) => c.startsWith("solana:"))) return "does not support Solana";
  if (!hasFeature(w, CONNECT)) return "does not offer a standard connect";
  if (!hasFeature(w, SIGN_TX) && !hasFeature(w, SIGN_AND_SEND)) return "cannot sign transactions";
  return null;
}

export interface WalletCensus {
  registered: number;
  usable: number;
  rejected: string[];
}

/**
 * Everything that registered, and what became of it. Separates "nothing is
 * installed" from "something is installed but is locked, on the wrong network,
 * or not a Solana wallet" -- two situations with opposite remedies that the
 * old code printed identically.
 */
export function inspectWallets(): WalletCensus {
  const all = getWallets().get();
  const rejected = all
    .map((w) => ({ name: w.name, why: rejectionReason(w) }))
    .filter((r) => r.why !== null)
    .map((r) => `${r.name} (${r.why})`);
  // listWallets(), not `all.filter(usable)`: the census has to count the
  // injected providers too, or the panel says "nothing installed" on a browser
  // where the connect button is right there.
  return { registered: all.length, usable: listWallets().length, rejected };
}

/** Fires whenever a wallet extension registers or unregisters itself. */
export function onWalletsChanged(cb: () => void): () => void {
  const { on } = getWallets();
  const offReg = on("register", cb);
  const offUnreg = on("unregister", cb);
  return () => {
    offReg();
    offUnreg();
  };
}

function serialize(tx: AnyTx): Uint8Array {
  return tx instanceof VersionedTransaction
    ? tx.serialize()
    : new Uint8Array(tx.serialize({ requireAllSignatures: false, verifySignatures: false }));
}

function deserializeLike(original: AnyTx, bytes: Uint8Array): AnyTx {
  return original instanceof VersionedTransaction
    ? VersionedTransaction.deserialize(bytes)
    : Transaction.from(bytes);
}

/**
 * Connects an injected provider.
 *
 * Simpler than the standard path, not lesser: legacy providers take and return
 * web3.js transaction OBJECTS, so there is no serialise / deserialise round
 * trip and no chance of losing a field in it. The provider signs; this code
 * never sees a key (invariant 4).
 */
async function connectLegacy(name: string, chain: string): Promise<ConnectedWallet> {
  const found = findLegacyProviders(typeof window === "undefined" ? null : window)
    .find((l) => l.name === name);
  if (!found) throw new Error(`wallet "${name}" is not available`);
  const p: LegacyProvider = found.provider;

  const result = await p.connect();
  // Some providers return the key, others only set it on themselves.
  const raw =
    (result && typeof result === "object" && "publicKey" in result ? result.publicKey : null) ??
    p.publicKey;
  if (!raw) throw new Error(`${name} connected but reported no account`);
  const publicKey = new PublicKey(
    typeof (raw as { toBase58?: () => string }).toBase58 === "function"
      ? (raw as { toBase58: () => string }).toBase58()
      : (raw as string)
  );

  return {
    name,
    icon: "",
    publicKey,
    chains: [chain],
    async signAllTransactions(txs: AnyTx[]): Promise<AnyTx[]> {
      if (txs.length === 0) return [];
      if (typeof p.signAllTransactions === "function") {
        const out = (await p.signAllTransactions(txs)) as AnyTx[];
        if (out.length !== txs.length) {
          throw new Error(`wallet returned ${out.length} signed transactions for ${txs.length} inputs`);
        }
        return out;
      }
      if (typeof p.signTransaction !== "function") {
        throw new Error(`${name} cannot sign transactions`);
      }
      // One at a time is a worse experience -- one approval per transaction --
      // but it is correct, and refusing outright would be worse still.
      const signed: AnyTx[] = [];
      for (const tx of txs) signed.push((await p.signTransaction(tx)) as AnyTx);
      return signed;
    },
    async disconnect() {
      if (typeof p.disconnect === "function") await p.disconnect();
    },
  };
}

export async function connectWallet(id: string, chain: string): Promise<ConnectedWallet> {
  if (isLegacyId(id)) return connectLegacy(legacyNameFromId(id), chain);

  const wallet = getWallets()
    .get()
    .find((w) => w.name === id && usable(w));
  if (!wallet) throw new Error(`wallet "${id}" is not available`);

  const connectFeature = wallet.features[CONNECT] as {
    connect: () => Promise<{ accounts: readonly WalletAccount[] }>;
  };
  const { accounts } = await connectFeature.connect();
  const account = accounts[0];
  if (!account) throw new Error(`${wallet.name} returned no accounts`);

  if (!hasFeature(wallet, SIGN_TX)) {
    throw new Error(
      `${wallet.name} does not support solana:signTransaction. This app builds and ` +
        `inspects transactions before sending, so sign-and-send-only wallets are not supported.`
    );
  }
  const signFeature = wallet.features[SIGN_TX] as {
    signTransaction: (
      ...inputs: { account: WalletAccount; transaction: Uint8Array; chain?: string }[]
    ) => Promise<readonly { signedTransaction: Uint8Array }[]>;
  };

  return {
    name: wallet.name,
    icon: wallet.icon,
    publicKey: new PublicKey(account.publicKey),
    chains: account.chains,
    async signAllTransactions(txs: AnyTx[]): Promise<AnyTx[]> {
      if (txs.length === 0) return [];
      const outputs = await signFeature.signTransaction(
        ...txs.map((tx) => ({ account, transaction: serialize(tx), chain }))
      );
      if (outputs.length !== txs.length) {
        throw new Error(
          `wallet returned ${outputs.length} signed transactions for ${txs.length} inputs`
        );
      }
      return outputs.map((o, i) => deserializeLike(txs[i], o.signedTransaction));
    },
    async disconnect() {
      if (hasFeature(wallet, DISCONNECT)) {
        await (wallet.features[DISCONNECT] as { disconnect: () => Promise<void> }).disconnect();
      }
    },
  };
}
